import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import Pagination from "../components/Pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  House,
  Plus,
  Spinner,
  Circuitry,
  User,
  ArrowLeft,
  Link as LinkIcon,
  CurrencyCircleDollar,
  ArrowsClockwise,
  Pencil,
  Trash,
  Lightning,
  MagnifyingGlass,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UnitsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const hasPerm = (key) => !isStaff || user?.permissions?.[key] !== false;
  const { apartmentId } = useParams();
  const [apartment, setApartment] = useState(null);
  const [units, setUnits] = useState([]);
  const [devices, setDevices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDeviceDialog, setAssignDeviceDialog] = useState(false);
  const [assignTenantDialog, setAssignTenantDialog] = useState(false);
  const [assignTariffDialog, setAssignTariffDialog] = useState(false);
  const [changeTenantDialog, setChangeTenantDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [editUnitDialog, setEditUnitDialog] = useState(false);
  const [editUnitTarget, setEditUnitTarget] = useState(null);
  const [editUnitData, setEditUnitData] = useState({ name: "", floor: "" });
  const [topupDialog, setTopupDialog] = useState(false);
  const [topupUnit, setTopupUnit] = useState(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupPreview, setTopupPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(() => {
    if (isStaff) {
      if (user?.permissions?.payment_cash !== false) return "cash";
      if (user?.permissions?.payment_bank !== false) return "bank";
    }
    return "cash";
  });
  const [customerSettings, setCustomerSettings] = useState({ topup_min_amount: 10, topup_max_amount: 1000 });
  const [searchQuery, setSearchQuery] = useState("");
  const [unitPage, setUnitPage] = useState(1);
  const [unitPageSize, setUnitPageSize] = useState(12);
  const [formData, setFormData] = useState({
    name: "",
    floor: "",
  });
  const [changeTenantData, setChangeTenantData] = useState({
    clear_old_balance: true,
    clear_device_kwh: true,
    mode: "existing", // "existing" or "new"
    new_tenant_id: "",
    new_tenant_email: "",
    new_tenant_password: "",
    new_tenant_name: "",
    new_tenant_phone: "",
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentId]);

  const fetchData = async () => {
    try {
      const [apartmentsRes, unitsRes, devicesRes, tenantsRes, tariffsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/apartments`),
        axios.get(`${API_URL}/api/apartments/${apartmentId}/units`),
        axios.get(`${API_URL}/api/devices`),
        axios.get(`${API_URL}/api/tenants`),
        axios.get(`${API_URL}/api/tariffs`),
        axios.get(`${API_URL}/api/customer/settings`).catch(() => ({ data: {} })),
      ]);
      
      const apt = apartmentsRes.data.find(a => a.id === apartmentId);
      setApartment(apt);
      setUnits(unitsRes.data);
      setDevices(devicesRes.data);
      setTenants(tenantsRes.data);
      setTariffs(tariffsRes.data);
      if (settingsRes.data) {
        setCustomerSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      toast.error("Veriler yuklenirken hata olustu");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUnit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await axios.post(`${API_URL}/api/units`, {
        apartment_id: apartmentId,
        ...formData,
      });
      toast.success("Daire başarıyla oluşturuldu");
      setDialogOpen(false);
      setFormData({ name: "", floor: "" });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleAssignDevice = async (deviceId) => {
    if (!selectedUnit) return;
    setFormLoading(true);

    try {
      await axios.put(`${API_URL}/api/units/${selectedUnit.id}/assign-device?device_id=${deviceId}`);
      toast.success("Sayac daireye atandi");
      setAssignDeviceDialog(false);
      setSelectedUnit(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleTopupPreview = async (amount) => {
    const tl = amount || parseFloat(topupAmount);
    if (!tl || tl <= 0 || !topupUnit?.tenant_id) { setTopupPreview(null); return; }
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/tenants/${topupUnit.tenant_id}/topup-preview?tl_amount=${tl}`);
      setTopupPreview(res.data);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data.detail);
      } else {
        toast.error(getErrorMessage(err));
      }
      setTopupPreview(null);
    } finally { setPreviewLoading(false); }
  };

  const handleQuickTopup = async () => {
    if (!topupUnit?.tenant_id || !topupPreview) return;
    setFormLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/tenants/${topupUnit.tenant_id}/add-balance?amount=${topupPreview.tl_amount}&payment_method=${paymentMethod}`);
      toast.success(res.data.message || "Bakiye yuklendi");
      setTopupDialog(false);
      setTopupUnit(null);
      setTopupAmount("");
      setTopupPreview(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleAssignTenant = async (tenantId) => {
    if (!selectedUnit) return;
    setFormLoading(true);

    try {
      await axios.put(`${API_URL}/api/units/${selectedUnit.id}/assign-tenant?tenant_id=${tenantId}`);
      toast.success("Kiracı daireye atandı");
      setAssignTenantDialog(false);
      setSelectedUnit(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleRemoveTenant = async (unitId) => {
    try {
      await axios.put(`${API_URL}/api/units/${unitId}/remove-tenant`);
      toast.success("Kiraci daireden cikarildi");
      fetchData();
    } catch (error) {
      toast.error("Islem basarisiz");
    }
  };

  const handleRemoveDevice = async (unitId) => {
    if (!window.confirm("Sayaci daireden cikarmak istediginize emin misiniz?")) return;
    try {
      await axios.put(`${API_URL}/api/units/${unitId}/remove-device`);
      toast.success("Sayac daireden cikarildi");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openEditUnitDialog = (unit) => {
    setEditUnitTarget(unit);
    setEditUnitData({ name: unit.name, floor: unit.floor || "" });
    setEditUnitDialog(true);
  };

  const handleUpdateUnit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/units/${editUnitTarget.id}`, editUnitData);
      toast.success("Daire guncellendi");
      setEditUnitDialog(false);
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const handleDeleteUnit = async (unitId, unitName) => {
    if (!window.confirm(`${unitName} dairesini silmek istediginize emin misiniz?`)) return;
    try {
      await axios.delete(`${API_URL}/api/units/${unitId}`);
      toast.success("Daire silindi");
      fetchData();
    } catch (error) { toast.error(getErrorMessage(error)); }
  };

  const openChangeTenantDialog = (unit) => {
    setSelectedUnit(unit);
    setChangeTenantData({
      clear_old_balance: true,
      clear_device_kwh: true,
      mode: "new",
      new_tenant_id: "",
      new_tenant_email: "",
      new_tenant_password: "",
      new_tenant_name: "",
      new_tenant_phone: "",
    });
    setChangeTenantDialog(true);
  };

  const handleChangeTenant = async () => {
    if (!selectedUnit) return;
    setFormLoading(true);
    try {
      const payload = {
        clear_old_balance: changeTenantData.clear_old_balance,
        clear_device_kwh: changeTenantData.clear_device_kwh,
      };
      if (changeTenantData.mode === "existing") {
        if (!changeTenantData.new_tenant_id) {
          toast.error("Kiraci secin");
          setFormLoading(false);
          return;
        }
        payload.new_tenant_id = changeTenantData.new_tenant_id;
      } else {
        if (!changeTenantData.new_tenant_email || !changeTenantData.new_tenant_password || !changeTenantData.new_tenant_name) {
          toast.error("Yeni kiraci bilgilerini doldurun");
          setFormLoading(false);
          return;
        }
        payload.new_tenant_email = changeTenantData.new_tenant_email;
        payload.new_tenant_password = changeTenantData.new_tenant_password;
        payload.new_tenant_name = changeTenantData.new_tenant_name;
        payload.new_tenant_phone = changeTenantData.new_tenant_phone;
      }
      await axios.post(`${API_URL}/api/units/${selectedUnit.id}/change-tenant`, payload);
      toast.success("Kiraci basariyla degistirildi");
      setChangeTenantDialog(false);
      setSelectedUnit(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  // Available tenants for change (exclude current tenant of selected unit)
  const availableTenantsForChange = tenants.filter(t => t.id !== selectedUnit?.tenant_id);

  const handleAssignTariff = async (tariffId) => {
    if (!selectedUnit) return;
    setFormLoading(true);

    try {
      await axios.put(`${API_URL}/api/units/${selectedUnit.id}/assign-tariff?tariff_id=${tariffId}`);
      toast.success("Tarife daireye atandi");
      setAssignTariffDialog(false);
      setSelectedUnit(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleRemoveTariff = async (unitId) => {
    try {
      await axios.put(`${API_URL}/api/units/${unitId}/remove-tariff`);
      toast.success("Tarife daireden kaldirildi");
      fetchData();
    } catch (error) {
      toast.error("Islem basarisiz");
    }
  };

  // Available devices: unassigned ones + currently assigned to selected unit
  const availableDevices = devices.filter(d => !d.unit_id || d.unit_id === selectedUnit?.id);
  const allDevicesForAssign = devices;

  // Search filtering for units
  const filteredUnits = units.filter((u) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = u.name?.toLowerCase().includes(q);
    const floorMatch = u.floor?.toLowerCase().includes(q);
    const tenantMatch = u.tenant_name?.toLowerCase().includes(q);
    const deviceMatch = u.device_name?.toLowerCase().includes(q);
    return nameMatch || floorMatch || tenantMatch || deviceMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="units-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link to="/apartments" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-2">
            <ArrowLeft size={16} className="mr-1" />
            Apartmanlara Dön
          </Link>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            {apartment?.name || "Apartman"} - Daireler
          </h1>
          <p className="text-slate-500 mt-1">{apartment?.address || "Adres belirtilmemiş"}</p>
        </div>
        {hasPerm("unit_add") && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-unit-btn">
              <Plus size={18} className="mr-2" />
              Daire Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("units.add_title")}</DialogTitle>
              <DialogDescription>{t("units.add_desc")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUnit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("units.name_label")}</Label>
                  <Input
                    id="name"
                    placeholder="Örn: Daire 101"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="unit-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="floor">{t("units.floor_label")}</Label>
                  <Input
                    id="floor"
                    placeholder="Örn: 1. Kat"
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    data-testid="unit-floor-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="unit-submit-btn">
                  {formLoading ? "Oluşturuluyor..." : "Oluştur"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Search Bar */}
      {units.length > 0 && (
        <div className="relative w-full sm:max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Daire, kiraci veya sayac ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-9"
            data-testid="unit-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="unit-clear-search-btn">
              <X size={16} />
            </button>
          )}
          {searchQuery && (
            <p className="text-xs text-slate-500 mt-1">{filteredUnits.length} sonuc</p>
          )}
        </div>
      )}

      {/* Units Grid */}
      {units.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <House size={64} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Henüz daire eklenmemiş</h3>
            <p className="text-slate-500 mb-4">Bu apartmana daire ekleyerek başlayın</p>
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUnits.slice((unitPage - 1) * unitPageSize, unitPage * unitPageSize).map((unit) => (
            <Card key={unit.id} className="border-slate-200 shadow-sm" data-testid={`unit-card-${unit.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                      <House size={26} weight="duotone" className="text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{unit.name}</CardTitle>
                      {unit.floor && <p className="text-sm text-slate-500">{unit.floor}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {hasPerm("unit_edit") && (
                    <Button size="sm" variant="ghost" className="text-amber-600 h-7 w-7 p-0"
                      onClick={() => openEditUnitDialog(unit)} data-testid={`edit-unit-btn-${unit.id}`} title={t("common.edit")}>
                      <Pencil size={14} />
                    </Button>
                    )}
                    {hasPerm("unit_delete") && (
                    <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0"
                      onClick={() => handleDeleteUnit(unit.id, unit.name)} data-testid={`delete-unit-btn-${unit.id}`} title={t("common.delete")}>
                      <Trash size={14} />
                    </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Sayaç Bilgisi */}
                <div className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Circuitry size={18} className="text-slate-500" />
                      <span className="text-sm text-slate-600">Sayaç</span>
                    </div>
                    {unit.device_id ? (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-700">{unit.device_name}</Badge>
                        {hasPerm("device_reassign") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-indigo-600 hover:text-indigo-700"
                          onClick={() => { setSelectedUnit(unit); setAssignDeviceDialog(true); }}
                          data-testid={`change-device-btn-${unit.id}`}
                        >
                          <ArrowsClockwise size={14} className="mr-1" />
                          Degistir
                        </Button>
                        )}
                        {hasPerm("device_reassign") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-red-600 hover:text-red-700"
                          onClick={() => handleRemoveDevice(unit.id)}
                          data-testid={`remove-device-btn-${unit.id}`}
                        >
                          Cikar
                        </Button>
                        )}
                      </div>
                    ) : (
                      hasPerm("device_reassign") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedUnit(unit); setAssignDeviceDialog(true); }}
                        data-testid={`assign-device-btn-${unit.id}`}
                      >
                        <LinkIcon size={14} className="mr-1" />
                        Ata
                      </Button>
                      )
                    )}
                  </div>
                </div>

                {/* Kiraci Bilgisi */}
                <div className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User size={18} className="text-slate-500" />
                      <span className="text-sm text-slate-600">Kiraci</span>
                      {unit.tenant_id ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Dolu</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Bos</Badge>
                      )}
                    </div>
                    {unit.tenant_id ? (
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <Badge className="bg-purple-100 text-purple-700">{unit.tenant_name}</Badge>
                          <p className={`text-xs mt-0.5 font-medium ${(unit.tenant_balance || 0) > 0 ? "text-emerald-600" : "text-red-500"}`}
                            data-testid={`tenant-balance-${unit.id}`}>
                            ₺{(unit.tenant_balance || 0).toFixed(2)}
                          </p>
                        </div>
                        {hasPerm("tenant_edit") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-indigo-600 hover:text-indigo-700"
                          onClick={() => openChangeTenantDialog(unit)}
                          data-testid={`change-tenant-btn-${unit.id}`}
                        >
                          <ArrowsClockwise size={14} className="mr-1" />
                          Degistir
                        </Button>
                        )}
                        {hasPerm("tenant_remove") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-red-600 hover:text-red-700"
                          onClick={() => handleRemoveTenant(unit.id)}
                          data-testid={`remove-tenant-btn-${unit.id}`}
                        >
                          Cikar
                        </Button>
                        )}
                      </div>
                    ) : (
                      hasPerm("tenant_add") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedUnit(unit); setAssignTenantDialog(true); }}
                        data-testid={`assign-tenant-btn-${unit.id}`}
                      >
                        <LinkIcon size={14} className="mr-1" />
                        Ata
                      </Button>
                      )
                    )}
                  </div>
                </div>

                {/* Tarife Bilgisi */}
                <div className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CurrencyCircleDollar size={18} className="text-slate-500" />
                      <span className="text-sm text-slate-600">Tarife</span>
                    </div>
                    {unit.tariff_id ? (
                      <div className="flex items-center gap-2">
                        <Badge className="bg-amber-100 text-amber-700">{unit.tariff_name}</Badge>
                        {hasPerm("tariff_set") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-red-600 hover:text-red-700"
                          onClick={() => handleRemoveTariff(unit.id)}
                          data-testid={`remove-tariff-btn-${unit.id}`}
                        >
                          Kaldir
                        </Button>
                        )}
                      </div>
                    ) : (
                      hasPerm("tariff_set") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedUnit(unit); setAssignTariffDialog(true); }}
                        data-testid={`assign-tariff-btn-${unit.id}`}
                      >
                        <LinkIcon size={14} className="mr-1" />
                        Ata
                      </Button>
                      )
                    )}
                  </div>
                </div>

                {/* Sayac Bakiye + Hizli Yukleme */}
                {unit.device_id && (
                  <div className={`p-3 rounded-xl border ${
                    unit.device_kwh_balance != null && unit.device_kwh_balance <= 10
                      ? "bg-red-50 border-red-200"
                      : unit.device_kwh_balance != null && unit.device_kwh_balance <= 50
                      ? "bg-amber-50 border-amber-200"
                      : "bg-emerald-50 border-emerald-200"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lightning size={18} weight="fill" className={
                          unit.device_kwh_balance != null && unit.device_kwh_balance <= 10
                            ? "text-red-500"
                            : unit.device_kwh_balance != null && unit.device_kwh_balance <= 50
                            ? "text-amber-500"
                            : "text-emerald-500"
                        } />
                        <span className="text-sm font-semibold" data-testid={`unit-kwh-${unit.id}`}>
                          {unit.device_kwh_balance != null ? `${unit.device_kwh_balance.toFixed(1)} kWh` : "-- kWh"}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          unit.device_online ? "bg-emerald-200 text-emerald-800" : "bg-red-200 text-red-800"
                        }`}>
                          {unit.device_online ? "Online" : "Offline"}
                        </span>
                      </div>
                      {unit.tenant_id && hasPerm("topup_load") && (
                        <Button
                          size="sm"
                          className="h-7 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold"
                          onClick={() => { setTopupUnit(unit); setTopupAmount(""); setTopupPreview(null); setTopupDialog(true); }}
                          data-testid={`quick-topup-btn-${unit.id}`}
                        >
                          <Lightning size={14} className="mr-1" />
                          Yukle
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        <Pagination total={filteredUnits.length} page={unitPage} pageSize={unitPageSize} onPageChange={setUnitPage} onPageSizeChange={setUnitPageSize} />
        </>
      )}

      {/* Assign Device Dialog */}
      <Dialog open={assignDeviceDialog} onOpenChange={setAssignDeviceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedUnit?.device_id ? "Sayac Degistir" : t("units.assign_device")} - {selectedUnit?.name}</DialogTitle>
            <DialogDescription>
              {selectedUnit?.device_id
                ? `Mevcut sayac: ${selectedUnit?.device_name}. Yeni sayac secin.`
                : "Bu daireye atanacak sayaci secin"}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {availableDevices.length === 0 ? (
              <div className="text-center py-8">
                <Circuitry size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Atanabilir sayac yok</p>
                <Link to="/devices">
                  <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700">
                    Sayac Ekle
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableDevices.filter(d => d.id !== selectedUnit?.device_id).map((device) => (
                  <button
                    key={device.id}
                    onClick={() => handleAssignDevice(device.id)}
                    className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    data-testid={`select-device-${device.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Circuitry size={20} className="text-indigo-600" />
                      <div className="text-left">
                        <span className="font-medium text-slate-900">{device.name}</span>
                        {device.unit_id && device.unit_id !== selectedUnit?.id && (
                          <p className="text-xs text-amber-600">Baska dairede atanmis</p>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline">{device.name}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Tenant Dialog */}
      <Dialog open={assignTenantDialog} onOpenChange={setAssignTenantDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("units.assign_tenant")} - {selectedUnit?.name}</DialogTitle>
            <DialogDescription>Bu daireye atanacak kiracıyı seçin</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {tenants.length === 0 ? (
              <div className="text-center py-8">
                <User size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Henüz kiracı yok</p>
                <Link to="/tenants">
                  <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700">
                    Kiracı Ekle
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {tenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => handleAssignTenant(tenant.id)}
                    className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    data-testid={`select-tenant-${tenant.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <span className="text-purple-600 font-semibold text-sm">
                          {tenant.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900">{tenant.name}</p>
                        <p className="text-xs text-slate-500">{tenant.email}</p>
                      </div>
                    </div>
                    <Badge className={tenant.balance > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                      ₺{tenant.balance?.toFixed(2)}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Tariff Dialog */}
      <Dialog open={assignTariffDialog} onOpenChange={setAssignTariffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("units.assign_tariff")} - {selectedUnit?.name}</DialogTitle>
            <DialogDescription>Bu daireye atanacak tarifeyi secin</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {tariffs.length === 0 ? (
              <div className="text-center py-8">
                <CurrencyCircleDollar size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Henuz tarife yok</p>
                <Link to="/tariffs">
                  <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700">
                    Tarife Olustur
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {tariffs.map((tariff) => (
                  <button
                    key={tariff.id}
                    onClick={() => handleAssignTariff(tariff.id)}
                    className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
                    data-testid={`select-tariff-${tariff.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <CurrencyCircleDollar size={20} className="text-amber-600" />
                      <div>
                        <p className="font-medium text-slate-900">{tariff.name}</p>
                        <p className="text-xs text-slate-500">
                          {tariff.tiers.length === 1
                            ? `Tek Fiyat: ${"\u20ba"}${tariff.tiers[0].price_per_kwh.toFixed(4)}/kWh`
                            : `${tariff.tiers.length} Kademe`}
                        </p>
                      </div>
                    </div>
                    {tariff.is_active && (
                      <Badge className="bg-indigo-100 text-indigo-700">{t("common.active")}</Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Tenant Dialog */}
      <Dialog open={changeTenantDialog} onOpenChange={setChangeTenantDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("units.change_tenant")} - {selectedUnit?.name}</DialogTitle>
            <DialogDescription>
              Eski kiraci cikarilacak ve yeni kiraci atanacak
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Old Tenant Info */}
            {selectedUnit?.tenant_name && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="old-tenant-info">
                <p className="text-sm font-medium text-red-700 mb-2">Mevcut Kiraci: {selectedUnit.tenant_name}</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={changeTenantData.clear_old_balance}
                      onChange={(e) => setChangeTenantData({...changeTenantData, clear_old_balance: e.target.checked})}
                      className="rounded border-slate-300" data-testid="clear-balance-checkbox" />
                    Eski kiracinin TL bakiyesini sifirla
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={changeTenantData.clear_device_kwh}
                      onChange={(e) => setChangeTenantData({...changeTenantData, clear_device_kwh: e.target.checked})}
                      className="rounded border-slate-300" data-testid="clear-kwh-checkbox" />
                    Sayactaki kWh bakiyeyi sifirla
                  </label>
                </div>
              </div>
            )}

            {/* New Tenant Mode Selection */}
            <div className="flex gap-2" data-testid="tenant-mode-selector">
              <Button size="sm"
                variant={changeTenantData.mode === "new" ? "default" : "outline"}
                className={changeTenantData.mode === "new" ? "bg-indigo-600 hover:bg-indigo-700 flex-1" : "flex-1"}
                onClick={() => setChangeTenantData({...changeTenantData, mode: "new"})}
                data-testid="mode-new-btn">
                Yeni Kiraci Olustur
              </Button>
              <Button size="sm"
                variant={changeTenantData.mode === "existing" ? "default" : "outline"}
                className={changeTenantData.mode === "existing" ? "bg-indigo-600 hover:bg-indigo-700 flex-1" : "flex-1"}
                onClick={() => setChangeTenantData({...changeTenantData, mode: "existing"})}
                data-testid="mode-existing-btn">
                Mevcut Kiraci Sec
              </Button>
            </div>

            {/* New Tenant Form */}
            {changeTenantData.mode === "new" && (
              <div className="space-y-3" data-testid="new-tenant-form">
                <div className="space-y-1">
                  <Label>Ad Soyad *</Label>
                  <Input placeholder="Kiraci adi" value={changeTenantData.new_tenant_name}
                    onChange={(e) => setChangeTenantData({...changeTenantData, new_tenant_name: e.target.value})}
                    data-testid="new-tenant-name" />
                </div>
                <div className="space-y-1">
                  <Label>E-posta *</Label>
                  <Input type="email" placeholder="kiraci@email.com" value={changeTenantData.new_tenant_email}
                    onChange={(e) => setChangeTenantData({...changeTenantData, new_tenant_email: e.target.value})}
                    data-testid="new-tenant-email" />
                </div>
                <div className="space-y-1">
                  <Label>{t("units.password_required")}</Label>
                  <Input type="password" placeholder="Giris sifresi" value={changeTenantData.new_tenant_password}
                    onChange={(e) => setChangeTenantData({...changeTenantData, new_tenant_password: e.target.value})}
                    data-testid="new-tenant-password" />
                </div>
                <div className="space-y-1">
                  <Label>{t("common.phone")}</Label>
                  <Input placeholder="05xx xxx xxxx" value={changeTenantData.new_tenant_phone}
                    onChange={(e) => setChangeTenantData({...changeTenantData, new_tenant_phone: e.target.value})}
                    data-testid="new-tenant-phone" />
                </div>
              </div>
            )}

            {/* Existing Tenant Selection */}
            {changeTenantData.mode === "existing" && (
              <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="existing-tenant-list">
                {availableTenantsForChange.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Atanabilir baska kiraci yok</p>
                ) : (
                  availableTenantsForChange.map((t) => (
                    <button key={t.id}
                      onClick={() => setChangeTenantData({...changeTenantData, new_tenant_id: t.id})}
                      className={`w-full flex items-center justify-between p-3 border rounded-xl transition-colors text-left ${
                        changeTenantData.new_tenant_id === t.id ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-indigo-300"
                      }`}
                      data-testid={`change-select-tenant-${t.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <span className="text-purple-600 font-semibold text-sm">{t.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{t.name}</p>
                          <p className="text-xs text-slate-500">{t.email}</p>
                        </div>
                      </div>
                      {changeTenantData.new_tenant_id === t.id && (
                        <Badge className="bg-indigo-100 text-indigo-700">Secildi</Badge>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeTenantDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleChangeTenant}
              disabled={formLoading} data-testid="change-tenant-submit">
              {formLoading ? <Spinner size={16} className="animate-spin mr-2" /> : <ArrowsClockwise size={16} className="mr-2" />}
              Kiraci Degistir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog open={editUnitDialog} onOpenChange={setEditUnitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("units.edit_title")}</DialogTitle>
            <DialogDescription>{editUnitTarget?.name} bilgilerini guncelleyin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUnit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-unit-name">{t("units.name")}</Label>
                <Input id="edit-unit-name" value={editUnitData.name}
                  onChange={(e) => setEditUnitData({ ...editUnitData, name: e.target.value })}
                  required data-testid="edit-unit-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-unit-floor">{t("common.floor")}</Label>
                <Input id="edit-unit-floor" value={editUnitData.floor}
                  onChange={(e) => setEditUnitData({ ...editUnitData, floor: e.target.value })}
                  data-testid="edit-unit-floor-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditUnitDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="edit-unit-submit-btn">
                {formLoading ? t("common.updating") : t("common.update")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Topup Dialog - Full Flow */}
      <Dialog open={topupDialog} onOpenChange={(open) => { setTopupDialog(open); if (!open) { setTopupPreview(null); setTopupAmount(""); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightning size={20} weight="fill" className="text-indigo-600" />
              kWh Yukleme - {topupUnit?.name}
            </DialogTitle>
            <DialogDescription>
              {topupUnit?.tenant_name && `Kiraci: ${topupUnit.tenant_name}`}
              {topupPreview && ` | Tarife: ${topupPreview.tariff_name || "-"}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
            {/* Current kWh Balance */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
              <Lightning size={20} weight="fill" className={`${(topupUnit?.device_kwh_balance || 0) > 5 ? "text-emerald-500" : "text-red-500"}`} />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Mevcut Bakiye</p>
                <p className={`text-lg font-bold ${(topupUnit?.device_kwh_balance || 0) > 5 ? "text-emerald-700" : "text-red-700"}`}>
                  {topupUnit?.device_kwh_balance != null ? `${topupUnit.device_kwh_balance.toFixed(2)} kWh` : "-- kWh"}
                </p>
              </div>
              <span className={`text-[10px] px-2 py-1 rounded font-medium ${
                topupUnit?.device_online ? "bg-emerald-200 text-emerald-800" : "bg-red-200 text-red-800"
              }`}>
                {topupUnit?.device_online ? "Online" : "Offline"}
              </span>
            </div>

            {/* Preset TL Buttons */}
            <div className="flex gap-2" data-testid="quick-topup-presets">
              {[50, 100, 250, 500, 1000].map((val) => (
                <Button key={val} size="sm" variant="outline" className="flex-1 text-xs"
                  onClick={() => { setTopupAmount(val.toString()); handleTopupPreview(val); }}
                  data-testid={`quick-topup-preset-${val}`}>
                  {"\u20ba"}{val}
                </Button>
              ))}
            </div>

            {/* Payment Method Selection */}
            <div data-testid="quick-topup-payment-method">
              <p className="text-xs font-medium text-slate-600 mb-2">Odeme Yontemi</p>
              <div className="flex gap-2">
                {hasPerm("payment_cash") && (
                  <Button size="sm"
                    variant={paymentMethod === "cash" ? "default" : "outline"}
                    className={`flex-1 ${paymentMethod === "cash" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                    onClick={() => setPaymentMethod("cash")}
                    data-testid="quick-topup-payment-cash">
                    Nakit
                  </Button>
                )}
                {hasPerm("payment_bank") && (
                  <Button size="sm"
                    variant={paymentMethod === "bank" ? "default" : "outline"}
                    className={`flex-1 ${paymentMethod === "bank" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                    onClick={() => setPaymentMethod("bank")}
                    data-testid="quick-topup-payment-bank">
                    Havale/EFT
                  </Button>
                )}
              </div>
            </div>

            {/* Custom TL Amount */}
            <div className="flex gap-2">
              <Input type="number" step="0.01"
                min={customerSettings.topup_min_amount} max={customerSettings.topup_max_amount}
                placeholder={`TL (${customerSettings.topup_min_amount} - ${customerSettings.topup_max_amount})`}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                className="flex-1"
                data-testid="quick-topup-amount-input" />
              <Button variant="outline" onClick={() => handleTopupPreview()} disabled={previewLoading || !topupAmount}
                data-testid="quick-topup-preview-btn">
                {previewLoading ? <Spinner size={14} className="animate-spin" /> : "Hesapla"}
              </Button>
            </div>
            <p className="text-xs text-slate-400">Min: {customerSettings.topup_min_amount} TL - Max: {customerSettings.topup_max_amount} TL</p>

            {/* Preview Breakdown */}
            {topupPreview && (
              <div className="space-y-3">
                {topupPreview.debt_amount > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="quick-topup-debt-warning">
                    <p className="text-xs font-medium text-red-700">Odenmemis Borc: {"\u20ba"}{topupPreview.debt_amount.toFixed(2)}</p>
                    <p className="text-xs text-red-600">
                      Bu odemeden {"\u20ba"}{topupPreview.debt_deducted.toFixed(2)} borc icin kesilecek, kalan {"\u20ba"}{topupPreview.effective_tl.toFixed(2)} kWh icin kullanilacak
                    </p>
                  </div>
                )}

                {topupPreview.tier_warning && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs font-medium text-amber-700">{topupPreview.tier_warning}</p>
                    <p className="text-xs text-amber-600">Mevcut: Kademe {topupPreview.current_tier} | Sonrasi: Kademe {topupPreview.after_tier}</p>
                  </div>
                )}

                {topupPreview.breakdown && topupPreview.breakdown.length > 0 && (
                  <div className="border border-indigo-200 rounded-xl overflow-hidden" data-testid="quick-topup-breakdown">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-indigo-100/50">
                          <th className="text-left p-2 font-medium text-indigo-700">Kademe</th>
                          <th className="text-left p-2 font-medium text-indigo-700">Aralik</th>
                          <th className="text-right p-2 font-medium text-indigo-700">Birim Fiyat</th>
                          <th className="text-right p-2 font-medium text-indigo-700">kWh</th>
                          <th className="text-right p-2 font-medium text-indigo-700">Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topupPreview.breakdown.map((b, i) => (
                          <tr key={i} className="border-t border-indigo-100">
                            <td className="p-2 text-slate-700">{b.tier}. Kademe</td>
                            <td className="p-2 text-slate-600">{b.range}</td>
                            <td className="p-2 text-right text-slate-700">{"\u20ba"}{b.price_per_kwh}</td>
                            <td className="p-2 text-right font-medium text-slate-900">{b.kwh}</td>
                            <td className="p-2 text-right font-medium text-slate-900">{"\u20ba"}{b.cost}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-indigo-300 bg-indigo-50">
                          <td colSpan={3} className="p-2 font-semibold text-indigo-700">Toplam</td>
                          <td className="p-2 text-right font-bold text-indigo-900">{topupPreview.total_kwh} kWh</td>
                          <td className="p-2 text-right font-bold text-indigo-900">{"\u20ba"}{topupPreview.total_cost}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {topupPreview.total_kwh === 0 && topupPreview.debt_deducted > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                    <p className="text-sm font-medium text-amber-700">Tum tutar borca aktarilacak</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setTopupDialog(false); setTopupPreview(null); setTopupAmount(""); }}>{t("common.cancel")}</Button>
            {topupPreview ? (
              <Button onClick={handleQuickTopup} disabled={formLoading}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="quick-topup-submit-btn">
                {formLoading ? <Spinner size={16} className="animate-spin mr-2" /> : <Lightning size={16} weight="fill" className="mr-2" />}
                {topupPreview.total_kwh > 0
                  ? `${topupPreview.total_kwh} kWh Yukle (${"\u20ba"}${topupPreview.total_cost})`
                  : `${"\u20ba"}${topupPreview.debt_deducted?.toFixed(2)} Borc Ode`
                }
                {topupPreview.debt_deducted > 0 && topupPreview.total_kwh > 0 ? ` + ${"\u20ba"}${topupPreview.debt_deducted.toFixed(2)} borc` : ""}
              </Button>
            ) : (
              <Button disabled className="bg-slate-300" data-testid="quick-topup-submit-btn">
                <Lightning size={16} className="mr-2" /> Once Hesaplayin
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnitsPage;
